import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { mockAuthUsers } from '@/mocks/auth';
import { mockPartners } from '@/mocks/partners';
import { mockLeads } from '@/mocks/leads';
import { mockPayoutLines } from '@/mocks/payouts';
import { mockInvoices } from '@/mocks/invoices';
import { mockCapacitySignals } from '@/mocks/capacity';
import { mockOffers } from '@/mocks/offers';
import {
  Users,
  Crown,
  UserCircle,
  Eye,
  Shield,
  Building2,
  ArrowRight,
  Zap,
  Inbox,
  Euro,
  Activity,
  CheckCircle,
  Layers,
} from 'lucide-react';

interface AccountCard {
  user: typeof mockAuthUsers[0];
  partner: typeof mockPartners[0] | undefined;
  leadCount: number;
  payoutCount: number;
  invoiceCount: number;
  capCount: number;
}

const roleMeta: Record<string, { icon: React.ReactNode; color: string; bg: string; border: string; label: string }> = {
  operator:        { icon: <Shield size={14} />,  color: 'var(--text-secondary)', bg: 'var(--bg-input)', border: 'var(--border-primary)', label: 'Operator' },
  account_manager: { icon: <Users size={14} />,   color: 'var(--accent)',         bg: 'var(--accent-dim)', border: 'var(--accent-glow)', label: 'Account Manager' },
  partner_principal:{ icon: <Crown size={14} />,  color: 'var(--success-text)',   bg: 'var(--success-bg)', border: 'var(--success-border)', label: 'Partner Principal' },
  partner_staff:   { icon: <UserCircle size={14}/>,color: 'var(--warning-text)',   bg: 'var(--warning-bg)', border: 'var(--warning-border)', label: 'Partner Staff' },
  read_only:       { icon: <Eye size={14} />,    color: 'var(--text-muted)',       bg: 'var(--bg-input)', border: 'var(--border-primary)', label: 'Read Only' },
};

export default function DebugPage() {
  const { user, switchUser } = useAuth();
  const navigate = useNavigate();

  const accounts: AccountCard[] = mockAuthUsers.map(u => {
    const partner = u.partner_id ? mockPartners.find(p => p.id === u.partner_id) : undefined;
    const partnerOffers = u.partner_id ? mockOffers.filter(o => o.partner_id === u.partner_id).map(o => o.id) : [];
    return {
      user: u,
      partner,
      leadCount: u.partner_id ? mockLeads.filter(l => l.assigned_partner_id === u.partner_id && !l.deleted_at).length : 0,
      payoutCount: u.partner_id ? mockPayoutLines.filter(p => p.partner_id === u.partner_id).length : 0,
      invoiceCount: u.partner_id ? mockInvoices.filter(i => i.partner_id === u.partner_id).length : 0,
      capCount: partnerOffers.length > 0 ? mockCapacitySignals.filter(s => partnerOffers.includes(s.offer_id)).length : 0,
    };
  });

  const handleSwitch = (account: AccountCard) => {
    switchUser(account.user.role);
    // If it's a partner account, also set the correct partner_id by storing the full mock user
    if (account.user.partner_id) {
      localStorage.setItem('ln_mock_auth_user', JSON.stringify(account.user));
    }
    // Navigate to the right landing page
    const dest =
      account.user.role === 'partner_principal' || account.user.role === 'partner_staff'
        ? '/partner'
        : account.user.role === 'account_manager'
          ? '/leads'
          : '/dashboard';
    navigate(dest, { replace: true });
  };

  return (
    <div className="space-y-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>
            Demo Accounts
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-tertiary)' }}>
            Click any card to instantly preview that account&apos;s experience
          </p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border text-[11px]" style={{ borderColor: 'var(--border-primary)', color: 'var(--text-muted)' }}>
          <Zap size={12} />
          Currently: <span className="font-medium" style={{ color: 'var(--text-secondary)' }}>{user?.full_name || user?.email}</span>
        </div>
      </div>

      {/* Account grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {accounts.map((a) => {
          const meta = roleMeta[a.user.role] || roleMeta.operator;
          const isActive = user?.id === a.user.id;
          return (
            <button
              key={a.user.id}
              onClick={() => handleSwitch(a)}
              className={`text-left rounded-xl p-5 transition-all duration-300 relative group hover-card${isActive ? ' plan-btn-active' : ''}`}
            >
              {/* Active indicator */}
              {isActive && (
                <div className="absolute top-3 right-3 flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full" style={{ background: 'var(--accent-glow)', color: 'var(--accent)' }}>
                  <CheckCircle size={10} />
                  Active
                </div>
              )}

              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: meta.bg, border: `1px solid ${meta.border}`, color: meta.color }}
                >
                  {meta.icon}
                </div>
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                    {a.user.full_name}
                  </p>
                  <p className="text-[11px] truncate" style={{ color: 'var(--text-muted)' }}>
                    {a.user.email}
                  </p>
                </div>
              </div>

              {/* Role badge */}
              <div className="flex items-center gap-2 mb-3">
                <span
                  className="text-[10px] font-medium px-2 py-0.5 rounded-full border"
                  style={{ background: meta.bg, color: meta.color, borderColor: meta.border }}
                >
                  {meta.label}
                </span>
                {a.partner && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full border flex items-center gap-1"
                    style={{ background: 'var(--bg-input)', color: 'var(--text-tertiary)', borderColor: 'var(--border-primary)' }}
                  >
                    <Building2 size={9} />
                    {a.partner.display_name}
                  </span>
                )}
              </div>

              {/* Data stats row */}
              {a.user.partner_id && (
                <div className="grid grid-cols-3 gap-2">
                  <div className="flex items-center gap-1.5 text-[11px]" style={{ color: 'var(--text-muted)' }}>
                    <Inbox size={11} />
                    <span className="font-medium" style={{ color: 'var(--text-secondary)' }}>{a.leadCount}</span>
                    <span>leads</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-[11px]" style={{ color: 'var(--text-muted)' }}>
                    <Euro size={11} />
                    <span className="font-medium" style={{ color: 'var(--text-secondary)' }}>{a.payoutCount}</span>
                    <span>payouts</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-[11px]" style={{ color: 'var(--text-muted)' }}>
                    <Layers size={11} />
                    <span className="font-medium" style={{ color: 'var(--text-secondary)' }}>{a.invoiceCount}</span>
                    <span>invoices</span>
                  </div>
                </div>
              )}

              {/* Arrow hint */}
              <div className="flex items-center gap-1 mt-4 text-[11px] font-medium transition-colors"
                style={{ color: isActive ? 'var(--accent)' : 'var(--text-muted)' }}
              >
                {isActive ? 'Currently viewing' : 'Click to switch'}
                <ArrowRight size={12} className="transition-transform group-hover:translate-x-0.5" />
              </div>
            </button>
          );
        })}
      </div>

      {/* Quick stats overview */}
      <div className="rounded-xl p-5" style={{ background: 'var(--bg-surface-raised)', border: '1px solid var(--border-primary)' }}>
        <h2 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-secondary)' }}>Mock Dataset Overview</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Leads', value: mockLeads.filter(l => !l.deleted_at).length, icon: <Inbox size={14} /> },
            { label: 'Partners', value: mockPartners.length, icon: <Building2 size={14} /> },
            { label: 'Payout Lines', value: mockPayoutLines.length, icon: <Euro size={14} /> },
            { label: 'Capacity Signals', value: mockCapacitySignals.length, icon: <Activity size={14} /> },
          ].map((s, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)', color: 'var(--text-muted)' }}>
                {s.icon}
              </div>
              <div>
                <p className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>{s.value}</p>
                <p className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>{s.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}